/**
 * Субтитры в нашем плеере: параметры включения и выбор ученика.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ КОД ВНУТРИ КОМПОНЕНТА. Проверки в этом проекте
 * собираются по образцу `src/**\/*.test.ts` — файл `.test.tsx` не запустится
 * и не пожалуется (см. `vitest.config.ts`). Логика, спрятанная в `.tsx`,
 * проверяемой не будет вовсе.
 *
 * ЗАЧЕМ ЭТО НУЖНО УЧЕНИКУ. Родные кнопки YouTube у нас отключены (`controls=0`),
 * чтобы кадр не выглядел как YouTube. Побочный итог: включить субтитры стало
 * нечем — своей кнопки не было, чужую отобрали.
 *
 * ⚠️ ЕСТЬ ЛИ У РОЛИКА СУБТИТРЫ — УЗНАТЬ НЕЛЬЗЯ, И ПЫТАТЬСЯ НЕ НАДО.
 * Список дорожек плеера (`getOption('captions','tracklist')`) отдаёт ТОЛЬКО
 * опубликованные дорожки, а `cc_load_policy` показывает и машинные. Замерено
 * 09.09.2026: у ролика с машинной И опубликованной дорожкой (`EDap9qxb96k`)
 * список вернул ровно одну — опубликованную; у ролика с двумя машинными
 * (`AOpt53VpW4o`) список пуст, а субтитры при этом идут.
 *
 * Здесь стояла проверка «показывать кнопку только при непустом списке» — и она
 * была ОШИБКОЙ: «список пуст» приняли за «субтитров нет». Заказчик 09.09.2026
 * прислал кадр с идущими субтитрами и вопросом «а почему тут нет кнопок?».
 * Выходило худшее: субтитры идут, а выключить их нечем. Теперь кнопка
 * безусловна — на 4 роликах из 131 она нажмётся впустую, это дешевле.
 *
 * ⚠️ ЧЕГО ЗДЕСЬ НЕТ И НЕ ДОЛЖНО ПОЯВИТЬСЯ: выбора языка. Из 131 ролика курса у
 * 127 дорожка ровно одна, казахская есть у одного. Выбирать не из чего.
 * Автоперевод YouTube (`translationLanguage`) дал бы 156 языков, но это
 * машинный перевод машинного распознавания речи — решением владельца такого
 * ученику не обещаем.
 *
 * Публикация машинных дорожек в YouTube Studio остаётся в работе отдельно
 * (`docs/YOUTUBE-CAPTIONS-TODO.md`): она даёт вычитанный текст вместо
 * машинного, а не саму возможность включить субтитры.
 */

/**
 * Параметры плеера для включённых или выключенных субтитров.
 *
 * ⚠️ ПОЧЕМУ ПАРАМЕТРЫ СОЗДАНИЯ, А НЕ `setOption`. Переключать субтитры на живом
 * плеере не выходит: проверено в браузере 09.09.2026 —
 * `setOption('captions','track',…)` дорожку не ставит, `getOption(…,'track')`
 * остаётся пустым. Единственный работающий путь — **пересоздать плеер** с этими
 * параметрами, вернув позицию через `getCurrentTime`/`seekTo`. Некрасиво, зато
 * проверено глазами: субтитры появляются и пропадают.
 *
 * ⚠️ `cc_lang_pref: 'en'` — курс английский. Без него YouTube выбирает дорожку
 * по языку браузера, и на ролике с парой en+ru ученику доставались русские
 * субтитры (опыт 3, 09.09.2026). Где английской дорожки нет, параметр остаётся
 * без последствий: покажется та, что есть.
 *
 * ⚠️ `hl: 'en'` — ради чужих роликов, где у YouTube ТОЛЬКО машинная дорожка
 * (`W8dPicyHvWw`, `LDubaaZWtH4`, 10.09.2026). `cc_lang_pref` выбирает среди
 * опубликованных дорожек, а машинную плеер подбирает по языку своего интерфейса —
 * у ученика с русским браузером она не находилась, и субтитры не шли. `hl` ставит
 * интерфейс плеера на английский. Кнопок YouTube у нас не видно (`controls=0`),
 * так что больше этот параметр ни на что не влияет. Выключенным субтитрам язык
 * интерфейса не нужен — не передаём. Действует ли он на данный ролик, видно только
 * в обычном браузере: в браузере-автомате YouTube ролики не играет.
 */
export function captionsPlayerVars(on: boolean): Record<string, string | number> {
  return on ? { cc_load_policy: 1, cc_lang_pref: 'en', hl: 'en' } : { cc_load_policy: 0 };
}

// ---------------------------------------------------------------------------
// Выбор ученика
// ---------------------------------------------------------------------------

/**
 * Ключ настройки. Префикс `onebilim:` — как у остальных личных переключателей
 * (`onebilim:haptics`, `onebilim:reminders`, `onebilim:biometrics`).
 *
 * Почему `localStorage`, а не `appConfig`: тот приходит с сервера и означает
 * «включена ли возможность вообще», а это личный выбор одного человека на одном
 * устройстве. Секрета в нём нет, защищённое хранилище не нужно.
 */
const PREF_KEY = 'onebilim:captions';

/** Записан ли выбор. Нужно там, где умолчание приходит извне — см. `/embed`. */
export function captionsPrefStored(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== null;
  } catch {
    return false;
  }
}

/** По умолчанию субтитры ВЫКЛЮЧЕНЫ: их включают, когда нужно, а не наоборот. */
export function captionsPrefEnabled(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === 'on';
  } catch {
    // Приватный режим браузера — молча остаёмся на умолчании.
    return false;
  }
}

export function setCaptionsPref(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* приватный режим — выбор проживёт до перезагрузки страницы */
  }
}
